/**
 * The six angle shots of a monthly, in walk-round order (PLAN §6 step 3). Optional and prompted
 * (decision 3): each tile has the camera, "From gallery", a status dot and Retry. A re-check takes
 * none.
 */

import { Loader2, RotateCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { SLOT_LABEL, SLOT_ORDER, type PhotoSlot } from '@/lib/inspections';
import { PhotoThumb } from '../PhotoZoom';
import PhotoInputs from './PhotoInputs';
import type { PhotoUpload } from './usePhotoUploads';

interface Props {
    /** The uploaded URL per slot, from the draft. */
    stored: Partial<Record<PhotoSlot, string>>;
    uploads: Record<string, PhotoUpload>;
    onPick: (slot: PhotoSlot, file: File) => void;
    onRetry: (slot: PhotoSlot) => void;
}

export function StatusDot({ status, stored }: { status: PhotoUpload['status'] | undefined; stored: boolean }) {
    const state = status ?? (stored ? 'done' : undefined);
    const tone =
        state === 'done' ? 'bg-emerald-500' : state === 'error' || state === 'unreadable' ? 'bg-red-500' : state ? 'bg-amber-500' : 'bg-muted-foreground/30';
    const label =
        state === 'done' ? 'Uploaded' : state === 'error' ? 'Upload failed' : state === 'unreadable' ? 'Cannot read photo' : state === 'preparing' ? 'Preparing' : state === 'uploading' ? 'Uploading' : 'No photo';
    return (
        <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
            <span aria-hidden className={cn('h-2 w-2 rounded-full', tone, (state === 'uploading' || state === 'preparing') && 'animate-pulse')} />
            {label}
        </span>
    );
}

export default function PhotoSlots({ stored, uploads, onPick, onRetry }: Props) {
    const taken = SLOT_ORDER.filter((s) => stored[s]).length;
    return (
        <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
                {taken} of 6 taken. Walk round the car: front with the plate, driver side, rear with the plate, passenger side, then the
                dashboard with the engine running and the interior.
            </p>
            <ul className="grid grid-cols-2 gap-3 lg:grid-cols-3">
                {SLOT_ORDER.map((slot, i) => {
                    const upload = uploads[slot];
                    const url = upload?.url ?? stored[slot] ?? null;
                    const preview = upload?.previewUrl ?? null;
                    const working = upload?.status === 'preparing' || upload?.status === 'uploading';
                    return (
                        <li key={slot} className="flex flex-col gap-2 rounded-xl border bg-card p-2.5">
                            <div className="relative aspect-[4/3] overflow-hidden rounded-lg bg-muted">
                                {url || preview ? (
                                    <PhotoThumb url={url ?? preview!} previewUrl={preview} alt={SLOT_LABEL[slot]} caption={SLOT_LABEL[slot]} className="h-full w-full rounded-lg border-0" />
                                ) : (
                                    <span className="absolute inset-0 flex items-center justify-center text-3xl font-semibold text-muted-foreground/40">{i + 1}</span>
                                )}
                                {working && (
                                    <span className="absolute inset-0 flex items-center justify-center bg-background/50">
                                        <Loader2 className="h-6 w-6 animate-spin" aria-hidden />
                                    </span>
                                )}
                            </div>
                            <p className="text-sm font-medium leading-tight">{SLOT_LABEL[slot]}</p>
                            <StatusDot status={upload?.status} stored={!!stored[slot]} />
                            {upload?.status === 'unreadable' && <p className="text-xs text-red-700 dark:text-red-400">{upload.error}</p>}
                            {upload?.status === 'error' && (
                                <Button type="button" size="sm" variant="outline" onClick={() => onRetry(slot)}>
                                    <RotateCw aria-hidden />
                                    Retry upload
                                </Button>
                            )}
                            <PhotoInputs compact cameraLabel={url ? 'Retake' : 'Camera'} onFile={(f) => onPick(slot, f)} disabled={working} />
                        </li>
                    );
                })}
            </ul>
        </div>
    );
}
